import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ChipModule } from 'primeng/chip';
import {
  BehaviorSubject,
  finalize,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { CollapseLayoutService } from 'src/app/shared/services/collapseLayout.service';
import { HomeService } from 'src/app/shared/services/home.service';
import { RecentAlertTab } from 'src/app/shared/services/intervention-filter.service';
import { InterventionService } from 'src/app/shared/services/intervention.service';
import { AppConstant } from 'src/app/shared/utilities/app.constant';
import { CustomExpandDialogComponent } from '../../../shared/components/custom-expand-dialog/custom-expand-dialog.component';
import { InterventionFilterService } from '../../../shared/services/intervention-filter.service';
import { NofiticationItemV2Component } from '../nofitication-item-v2/nofitication-item-v2.component';

/**
 * Redesigned Recent Alerts panel.
 *
 * This component owns only the v2 presentation concerns and the data wiring it
 * needs. It does not inherit from NotificationListComponent because that legacy
 * base class mixes list state, infinite-scroll behavior, and service coupling
 * that is not part of the v2 contract. Keeping this component standalone makes
 * the dependency direction explicit and reduces the risk of future breakage when
 * that legacy component is removed.
 */
@Component({
  selector: 'app-notification-list-v2',
  templateUrl: './notification-list-v2.component.html',
  styleUrls: ['./notification-list-v2.component.scss'],
  imports: [
    NgClass,
    ChipModule,
    CustomExpandDialogComponent,
    NofiticationItemV2Component,
    AsyncPipe,
    SvgIconComponent,
  ],
})
export class NotificationListV2Component implements OnInit, OnDestroy {
  @Input() isCollapse = true;
  @Input() call: boolean = false;

  @Output() minimizeNotification = new EventEmitter<boolean>();

  activeTab: RecentAlertTab = 'all';
  searchKeyword = '';
  rawLogs: any[] = [];
  interventionLogs$: any;
  interventionLogsActive$: any;
  interventionLogLength = 0;
  interventionLogTotalLength = 0;
  backendTabTotals = {
    all: 0,
    action: 0,
    unread: 0,
    completed: 0,
  };
  chipFilter = '';
  totalUnread = 0;
  isLoadingLoadMore$ = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();
  // Triggers tab reloads; switchMap cancels stale in-flight requests so an
  // older tab's response can't overwrite newer data.
  private readonly reloadTrigger$ = new Subject<void>();

  // Caches displayLogs so it's only recomputed when rawLogs/activeTab/keyword
  // actually change, instead of on every template read.
  private _displayLogsCache: {
    rawLogs: any[];
    activeTab: RecentAlertTab;
    searchKeyword: string;
    result: any[];
  } | null = null;

  @ViewChild('infiniteScroll') infiniteScroll?: ElementRef;

  constructor(
    private readonly _interventionServiceV2: InterventionService,
    private readonly _collapseLayoutService: CollapseLayoutService,
    private readonly _homeService: HomeService,
    @Inject(InterventionFilterService)
    private readonly _interventionFilterService: InterventionFilterService
  ) {}

  get tabs() {
    return this._interventionFilterService.tabs;
  }

  ngOnInit(): void {
    this.activeTab =
      this._interventionServiceV2.getActiveRecentAlertTab?.() || 'all';

    this.interventionLogs$ = this._interventionServiceV2.interventionLogs$;
    this.interventionLogsActive$ =
      this._interventionServiceV2.interventionLogsActive$;

    this.interventionLogs$
      .pipe(takeUntil(this.destroy$))
      .subscribe((list: any[]) => {
        this.rawLogs = list || [];
        this.interventionLogLength = list?.length || 0;
      });

    this._interventionServiceV2.interventionLogsTotal$
      .pipe(takeUntil(this.destroy$))
      .subscribe((total: number) => {
        this.interventionLogTotalLength = total || 0;
      });

    this._interventionServiceV2.interventionLogsTabTotals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (tabTotals: {
          all: number;
          action: number;
          unread: number;
          completed: number;
        }) => {
          this.backendTabTotals = {
            all: tabTotals?.all || 0,
            action: tabTotals?.action || 0,
            unread: tabTotals?.unread || 0,
            completed: tabTotals?.completed || 0,
          };
        }
      );

    this._interventionServiceV2.totalUnread$
      .pipe(takeUntil(this.destroy$))
      .subscribe((total: number) => {
        this.totalUnread = total ?? 0;
      });

    this._interventionServiceV2.interventionLogsFilter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((logsFilter: { rigJournalId: string; rigName: string }) => {
        this.chipFilter = logsFilter?.rigName || '';
      });

    this._interventionServiceV2.RefreshRequired.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.reloadCurrentTab();
    });

    this.reloadTrigger$
      .pipe(
        switchMap(() => {
          this.isLoadingLoadMore$.next(true);
          return this._interventionServiceV2
            .getInterventionLogsFilter(
              this.buildFilterPayload(),
              0,
              AppConstant.ITEMS_PER_PAGE,
              this.activeTab
            )
            .pipe(finalize(() => this.isLoadingLoadMore$.next(false)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: any) => {
          this._interventionServiceV2.setInterventionLogs(response.items || []);
          this._interventionServiceV2.setInterventionLogsTotal(
            response.total || 0
          );
        },
        error: (error: any) => {
          console.log(error);
          this._interventionServiceV2.setInterventionLogs([]);
          this._interventionServiceV2.setInterventionLogsTotal(0);
        },
      });

    this.reloadCurrentTab();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  emitChangeSize(): void {
    this.isCollapse = !this.isCollapse;
    this.minimizeNotification.emit(this.isCollapse);
    this._collapseLayoutService.updateCollapseLayout(this.isCollapse);
  }

  @HostListener('window:scroll', ['$event'])
  scrollHandler(event: any): void {
    const scrollArea = this.infiniteScroll?.nativeElement;
    if (!scrollArea || this._interventionServiceV2.getTriggerRigCard()) {
      return;
    }

    if (
      scrollArea.scrollHeight <=
      scrollArea.offsetHeight + Math.abs(scrollArea.scrollTop) + 1
    ) {
      if (
        !this.isLoadingLoadMore$.getValue() &&
        scrollArea.scrollHeight > scrollArea.clientHeight &&
        this.interventionLogTotalLength >= AppConstant.ITEMS_PER_PAGE &&
        this.interventionLogLength !== this.interventionLogTotalLength
      ) {
        this.loadMore(this.interventionLogLength);
      }
    }
  }

  loadMore(skip: number): void {
    this.isLoadingLoadMore$.next(true);

    this._interventionServiceV2
      .getInterventionLogsFilter(
        this.buildFilterPayload(),
        skip,
        AppConstant.ITEMS_PER_PAGE,
        this.activeTab
      )
      .pipe(finalize(() => this.isLoadingLoadMore$.next(false)))
      .subscribe({
        next: (appendData: any) => {
          this._interventionServiceV2.loadMoreAlertLog(appendData);
        },
        error: (error: any) => {
          console.log(error);
          this._interventionServiceV2.setInterventionLogs([]);
          this._interventionServiceV2.setInterventionLogsTotal(0);
        },
      });
  }

  /** Logs after applying the active tab and keyword (presentation only). */
  get displayLogs(): any[] {
    const cache = this._displayLogsCache;
    if (
      cache &&
      cache.rawLogs === this.rawLogs &&
      cache.activeTab === this.activeTab &&
      cache.searchKeyword === this.searchKeyword
    ) {
      return cache.result;
    }

    const result = this._interventionFilterService.apply(
      this.rawLogs,
      this.activeTab,
      this.searchKeyword
    );

    this._displayLogsCache = {
      rawLogs: this.rawLogs,
      activeTab: this.activeTab,
      searchKeyword: this.searchKeyword,
      result,
    };

    return result;
  }

  get displayLogTotal(): number {
    if (this.activeTab === 'action') {
      return this.backendTabTotals.action;
    }
    if (this.activeTab === 'unread') {
      return this.backendTabTotals.unread;
    }
    if (this.activeTab === 'completed') {
      return this.backendTabTotals.completed;
    }
    return this.backendTabTotals.all || this.interventionLogTotalLength;
  }

  /**
   * Unread badge. "All" and "Unread" tabs use the backend `totalUnread`
   * (independent of search/filters/pagination). Other tabs (Action Required,
   * Completed) count unread rows belonging to that tab, capped by its total.
   */
  get newCount(): number {
    if (this.activeTab === 'all' || this.activeTab === 'unread') {
      return this.totalUnread;
    }

    const unreadInView = this._interventionFilterService.countNew(
      this.displayLogs
    );
    return Math.min(unreadInView, this.displayLogTotal);
  }

  setTab(tab: RecentAlertTab): void {
    if (this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;
    this._interventionServiceV2.setActiveRecentAlertTab(tab);
    this.reloadCurrentTab();
  }

  onKeywordChange(event: Event): void {
    this.searchKeyword = (event.target as HTMLInputElement).value || '';
  }

  private buildFilterPayload(): any {
    const homeFilter = this._homeService.getPayloadFilter() || {};
    const activeRigFilter = this._interventionServiceV2.getLogFilter?.() || {
      rigJournalId: '',
      rigName: '',
    };

    return {
      ...homeFilter,
      ...(activeRigFilter?.rigJournalId
        ? { rigJournalId: [activeRigFilter.rigJournalId] }
        : {}),
    };
  }

  private reloadCurrentTab(): void {
    this.reloadTrigger$.next();
  }

  onAlertOpened(event: { rigJournalId: string; alertId: string }): void {
    this._interventionServiceV2
      .markInterventionLogAsRead(event.alertId)
      .pipe(take(1))
      .subscribe({
        error: () => {
          // Keep navigation flow uninterrupted even if persistence fails.
        },
      });
  }
}
